import { CheckCircle2, FileStack, Route, Waypoints } from 'lucide-react';
import {
  MetricCard,
  PageHero,
  StatusPill,
} from '../ui/ScreenPrimitives';
import {
  extractQueueDebugGraph,
  extractQueueOutput,
  extractQueuePayload,
  extractQueueRoute,
  getQueueScreenMode,
} from '../../lib/appHelpers';

function ResultView({
  queue,
  lastProcessingJob,
  stage,
  validateReviewBeforeNavigation,
  renderSubmittedRequestSection,
  renderInputSection,
  renderCurrentProgressSection,
  renderProgressTimelineSection,
  renderOutputSection,
  renderExecutionNavigationSection,
  renderDebugGraphSection,
  renderReviewQuestionsSection,
  renderFeedbackSection,
  renderReferenceDocumentSection,
}) {
  const screenMode = getQueueScreenMode(queue);
  const payload = extractQueuePayload(queue);
  const pageTitle =
    payload?.inputMode === 'text' ? 'Request processing' : 'Document processing';
  const pageLede =
    screenMode === 'final'
      ? 'Review the final output for this completed request.'
      : screenMode === 'review'
        ? 'Processing is complete, but answers are still required before the result can fully finalize.'
        : payload?.inputMode === 'text'
          ? 'Track the submitted request until analysis is complete.'
          : 'Track the uploaded queue item until analysis is complete.';
  const stageTone =
    stage.type === 'warning'
      ? 'warning'
      : stage.type === 'loading'
        ? 'loading'
        : stage.type || 'neutral';

  void validateReviewBeforeNavigation;

  return (
    <section className="w-full max-w-[1380px] text-app-ink">
      <PageHero
        eyebrow="Queue Result"
        title={pageTitle}
        lede={pageLede}
        bearyMessage={stage.type === 'loading' ? 'Beary is fetching the latest queue result.' : undefined}
        bearyPreset="loading"
        action={<StatusPill tone={stageTone}>{stage.message}</StatusPill>}
      />

      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Queue status"
          value={queue?.status || 'Unknown'}
          note={queue?._id || 'Queue id unavailable'}
          icon={CheckCircle2}
        />
        <MetricCard
          label="Processing job"
          value={lastProcessingJob?._id ? 'Attached' : 'Pending'}
          note={lastProcessingJob?._id || queue?.processingJobId || 'Not started'}
          icon={Waypoints}
        />
        <MetricCard
          label="Output"
          value={extractQueueOutput(queue) ? 'Ready' : 'Waiting'}
          note={extractQueueRoute(queue) || 'No route reported yet'}
          icon={FileStack}
        />
        <MetricCard
          label="Debug graph"
          value={extractQueueDebugGraph(queue) ? 'Available' : 'Unavailable'}
          note={screenMode === 'processing' ? 'Live processing view' : 'Captured pipeline detail'}
          icon={Route}
        />
      </div>

      <div className="mt-6 space-y-6">
        {renderSubmittedRequestSection(queue)}
        {renderInputSection(queue)}
        {screenMode === 'processing' ? renderCurrentProgressSection(queue) : null}
        {screenMode === 'processing' ? renderProgressTimelineSection(queue) : null}
        {screenMode === 'final' || screenMode === 'review' || screenMode === 'failed'
          ? renderOutputSection(queue)
          : null}
        {screenMode === 'final' || screenMode === 'review'
          ? renderExecutionNavigationSection(queue)
          : null}
        {renderDebugGraphSection(queue)}
        {screenMode === 'review' ? renderReviewQuestionsSection(queue) : null}
        {screenMode !== 'processing' ? renderFeedbackSection(queue) : null}
        {renderReferenceDocumentSection(queue)}
      </div>
    </section>
  );
}

export default ResultView;
